<template>
    <v-card class="half-card" elevation="3">
        <v-tabs v-model="tab" bg-color="transparent" color="basil" grow>
            <v-tab value="notifications">
                Notifications
                <span v-if="notifications?.length">({{ unreadNotifications }})</span>
            </v-tab>
            <v-tab v-if="canSeeMessages" value="messages">
                Device Messages
                <span v-if="messages?.length">({{ totalMessages }})</span>
            </v-tab>
        </v-tabs>
        <v-window v-model="tab">
            <v-window-item value="notifications">
                <v-card color="basil" flat>
                    <v-card-text>
                        <p v-if="assetId === null">Please select an asset</p>
                        <v-row v-else>
                            <v-col>
                                <v-table density="compact">
                                    <thead>
                                    <tr>
                                        <th>Timestamp</th>
                                        <th>Type</th>
                                        <th>Title</th>
                                        <th>Description</th>
                                        <th v-if="user.type !== 'admin'">Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="(notification, index) in notifications"
                                        v-if="!loading && notifications.length" :key="index"
                                        :class="'text-left'+(!notification.read?' font-weight-bold':'')">
                                        <td>{{ formatDateTime(notification.event_timestamp) }}</td>
                                        <td>{{ alertLabel(notification) }}</td>
                                        <td>{{ notification.title }}</td>
                                        <td>
                                            <v-icon icon="mdi-information"
                                                    @click="showNotificationDetails(notification)"/>
                                        </td>
                                        <td v-if="user.type !== 'admin'">
                                            <v-btn :icon="notification.read?'mdi-email-open':'mdi-email'"
                                                   :title="notification.read?'Mark as unread':'Mark as read'"
                                                   size="small"
                                                   variant="flat"
                                                   @click="toggleRead(notification)"/>
                                        </td>
                                    </tr>
                                    <tr v-else>
                                        <td v-if="loading" colspan="5">
                                            <v-progress-circular color="primary" indeterminate/>
                                        </td>
                                        <td v-else colspan="5">No pending notifications</td>
                                    </tr>
                                    </tbody>
                                </v-table>

                                <div class="d-flex align-center pt-2">
                                    <div v-if="user.type !== 'admin'" class="d-flex ga-1">
                                        <v-btn color="green" size="x-small" variant="tonal"
                                               prepend-icon="mdi-email-open"
                                               :disabled="!notifications.length"
                                               @click="readAll">
                                            Mark all Read
                                        </v-btn>
                                        <v-btn color="red" size="x-small" variant="tonal"
                                               prepend-icon="mdi-notification-clear-all"
                                               :disabled="!notifications.length"
                                               @click="clearAll">
                                            Clear All
                                        </v-btn>
                                    </div>
                                    <v-spacer/>
                                    <v-pagination v-if="totalPages > 1" v-model="currentPage" :length="totalPages"
                                                  class="py-0" density="compact" @click="fetchNotifications"/>
                                </div>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-window-item>
            <v-window-item v-if="canSeeMessages" value="messages">
                <v-card color="basil" flat>
                    <v-card-text>
                        <p v-if="assetId === null">Please select an asset</p>
                        <v-table v-else density="compact">
                            <thead>
                            <tr>
                                <th>Timestamp</th>
                                <th>Event Triggered</th>
                                <th>Location</th>
                                <th>Voltage</th>
                                <th>RSSI</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="message in messages" v-if="!messagesLoading && messages?.length"
                                :key="message.id"
                                class="text-left">
                                <td>{{ formatDateTime(message.event_time) }}</td>
                                <td>{{ message.trigger_event }}</td>
                                <td>
                                    {{ `${message.latitude}, ${message.longitude}` }}
                                    <v-btn
                                        size="small"
                                        class="ms-2"
                                        variant="text"
                                        density="compact"
                                        icon="mdi-content-copy"
                                        @click="copyText(`${message.latitude}, ${message.longitude}`)"
                                    />
                                </td>
                                <td>{{ strOperations().formatVoltage(message.voltage1) }}/
                                    {{ strOperations().formatVoltage(message.voltage2) }}
                                </td>
                                <td>{{ message.rssi }}</td>
                            </tr>
                            <tr v-else>
                                <td v-if="messagesLoading" colspan="6">
                                    <v-progress-circular color="primary" indeterminate/>
                                </td>
                                <td v-else colspan="6">No recent messages</td>
                            </tr>
                            </tbody>
                        </v-table>

                        <v-pagination
                            class="py-0 pt-2"
                            density="compact"
                            v-if="messagesTotalPages > 1"
                            v-model="messagesCurrentPage"
                            :length="messagesTotalPages"
                            @click="fetchMessages"
                        ></v-pagination>
                    </v-card-text>
                </v-card>
            </v-window-item>
        </v-window>
    </v-card>


    <v-dialog v-if="dialogSwitch && selectedNotification" v-model="dialogSwitch" max-width="700" scrollable>
        <v-card>
            <v-card-title class="d-flex justify-space-between">
                <span>{{ selectedNotification.title || 'Alert Details' }}</span>
                <v-btn density="compact" icon="mdi-close" variant="flat" @click="dialogSwitch = false"/>
            </v-card-title>
            <v-card-text>
                <alert-payload-details :notification="selectedNotification"/>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {ref, watch} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {strOperations} from "@/composables/globals";
import {useClipboard} from "@/composables/clipboard";
import {useSnackbarStore} from "@/stores/snackBarStore";
import AlertPayloadDetails from "@/components/cards/AlertPayloadDetails.vue";
import {formatDateTime} from "@/composables/useTimezone";
import {alertLabel} from "@/composables/alertTypes";

const {user} = useAuthStore();
const messages = ref([]);
const tripIDs = ref(null);
const totalMessages = ref(null);
const {copyToClipboard} = useClipboard();
const unreadNotifications = ref(null);
const selectedNotification = ref(null);
const dialogSwitch = ref(false);
const notifications = ref([]);
const props = defineProps({
    assetId: Number,
    activeTab: Boolean,
});

const {showSnackbar} = useSnackbarStore();
// Device messages are for staff only; customers see notifications
const canSeeMessages = user?.type === 'admin' || !!user?.super;
const tab = ref(props.activeTab || !canSeeMessages ? "notifications" : "messages");

const loading = ref(false);
const totalPages = ref(1);
const currentPage = ref(1);
const messagesLoading = ref(false);
const messagesTotalPages = ref(1);
const messagesCurrentPage = ref(1);
const emits = defineEmits(['tabSwitched']);
const copyText = (textToCopy) => {
    copyToClipboard(textToCopy);
}

const showNotificationDetails = (notification) => {
    selectedNotification.value = notification;
    dialogSwitch.value = true;
}
const fetchMessages = () => {
    if (!props.assetId) return;
    messagesLoading.value = true;
    send('get', `assets/${props.assetId}/device-messages`, {
        trips: tripIDs.value ?? null,
        page: messagesCurrentPage.value,
        columns: JSON.stringify("id,message_type,trigger_event,event_time,voltage1,voltage2,rssi,latitude,longitude")
    })
        .then((response) => {
            tripIDs.value = response.data.data.trips;
            messages.value = response.data.data.messages.data ?? [];
            totalMessages.value = response.data.data.messages?.meta?.total ?? 1;
            messagesTotalPages.value = response.data.data.messages?.meta?.last_page ?? 1;
            messagesCurrentPage.value = response.data.data.messages?.meta?.current_page ?? 1;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Device messages could not be loaded', 'error')
        }).finally(() => messagesLoading.value = false)
}
const fetchNotifications = () => {
    let data = {
        page: currentPage.value,
        asset: props.assetId,
    };
    loading.value = true;
    send('get', `assets/notifications`, data)
        .then((response) => {
            unreadNotifications.value = response.data.data.unread_count;
            notifications.value = response.data.data.notifications.data ?? [];
            totalPages.value = response.data.data.notifications.meta.last_page;
            currentPage.value = response.data.data.notifications.meta.current_page;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Notifications could not be loaded', 'error')
        }).finally(() => loading.value = false)
}
const readAll = () => {
    send('post', 'notifications/read', {asset_id: props.assetId})
        .then((response) => {
            if (response.data.success) {
                notifications.value.forEach(n => n.read = true);
                unreadNotifications.value = 0;
                showSnackbar('All notifications marked as read');
            }
        })
        .catch(() => showSnackbar('Could not mark notifications as read', 'error'))
}

const clearAll = () => {
    if (!confirm('Are you sure you want to delete all notifications?')) return;
    send('delete', 'notifications/clear', {params: {asset_id: props.assetId}})
        .then((response) => {
            if (response.data.success) {
                notifications.value = [];
                unreadNotifications.value = 0;
                currentPage.value = 1;
                totalPages.value = 1;
                showSnackbar('All notifications cleared');
            }
        })
        .catch(() => showSnackbar('Could not clear notifications', 'error'))
}

const toggleRead = (notification) => {
    send('put', `notifications/${notification.id}`)
        .then((response) => {
            if (response.data.success) {
                notifications.value.map((item, index) => {
                    if (item.id === notification.id && response.data.data) {
                        notifications.value[index]['read'] = !notifications.value[index]['read'];
                    }
                });
                showSnackbar(response.data.message);
            }
        })
        .catch((error) => {
            console.log(error)
            showSnackbar(`"${notification.title}" Notification could not be marked read`, 'error')
        })

}
watch(() => props.activeTab, (newTab) => {
    newTab ? tab.value = 'notifications' : '';
})

watch(() => tab.value, (newTab) => {
    emits('tabSwitched', newTab === 'notifications');
    if (props.assetId) {
        if (newTab === 'messages' && !messages.value.length) {
            fetchMessages();
        } else if (newTab === 'notifications' && !notifications.value.length) {
            fetchNotifications();
        }
    }
});

watch(() => props.assetId, (newValue) => {
    messages.value = [];
    tripIDs.value = null;
    totalPages.value = 1;
    currentPage.value = 1;
    notifications.value = [];
    totalMessages.value = 0;
    unreadNotifications.value = 0;
    messagesTotalPages.value = 1;
    messagesCurrentPage.value = 1;
    if (newValue !== null) {
        if (tab.value === 'messages') {
            fetchMessages();
        } else if (tab.value === 'notifications') {
            fetchNotifications();
        }
    }
});
</script>
